import React, { forwardRef, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useStudentPermissions } from 'core/hooks';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../StudentDetail.styled';
import {
  AccountsPayableBlock,
  AgreementCard,
  ContactCard,
  GroupsCard,
  IStudent,
  OrphanInvoicesBlock,
} from '../parts';
import { StudentColors } from '../parts/studentColors';
import { AcademicPanel } from '../parts/AcademicPanel';
import { ActivityTimeline } from '../parts/ActivityTimeline';
import { FinanceExportButton } from '../parts/FinanceExportButton';

interface IOverviewTabProps {
  student: IStudent;
  onChanged?: () => void;
  /** Scroll anchor for the Accounts_Payable_Block (alert deep-link target). */
  payablesRef?: React.RefObject<HTMLDivElement>;
}

interface IOverviewTabRefs {
  orphansRef: React.RefObject<HTMLDivElement>;
}

/**
 * Dashboard-style overview — everything important on one scroll. Active
 * agreements are expanded by default; terminated ones collapsed but still
 * present (full audit trail in one place). The «Без договора» orphan-
 * invoices block lives at the bottom of the active section so the header
 * «Link Invoice» action has somewhere meaningful to scroll to.
 */
export const OverviewTab = memo(
  forwardRef<HTMLDivElement, IOverviewTabProps>(
    ({ student, onChanged, payablesRef }, orphansRef) => {
      const { t } = useTranslation();
      const { canExportFinance } = useStudentPermissions();

      const agreements = useMemo(
        () =>
          (student.agreements || []).filter((a): a is NonNullable<typeof a> =>
            Boolean(a),
          ),
        [student.agreements],
      );
      const active = agreements.filter((a) => !a.terminated);
      const terminated = agreements.filter((a) => a.terminated);

      return (
        <div style={{ marginTop: 16 }}>
          <L.TwoColumnGrid>
            <ContactCard student={student} />
            <GroupsCard student={student} />
          </L.TwoColumnGrid>

          {/* Dedicated academic + activity surfaces, separate from the
              financial blocks at all times (Req 8.1). */}
          <AcademicPanel studentId={student.id} />
          <ActivityTimeline student={student} />

          <ExportRow>
            <FinanceExportButton
              student={student}
              canExportFinance={canExportFinance}
            />
          </ExportRow>

          {active.length === 0 ? (
            <S.EmptyState>
              {t('pages.studentDetail.overview.noActiveAgreements')}
            </S.EmptyState>
          ) : (
            active.map((a) => (
              <AgreementCard
                key={a.id}
                agreement={a}
                student={student}
                studentId={student.id}
                studentEmail={student.user?.email ?? null}
                defaultExpanded
                onChanged={onChanged}
              />
            ))
          )}

          {terminated.length > 0 ? (
            <details style={{ marginTop: 8 }}>
              <TerminatedSummary>
                {t('pages.studentDetail.overview.terminatedSection', {
                  count: terminated.length,
                })}
              </TerminatedSummary>
              <div style={{ marginTop: 8 }}>
                {terminated.map((a) => (
                  <AgreementCard
                    key={a.id}
                    agreement={a}
                    student={student}
                    studentId={student.id}
                    studentEmail={student.user?.email ?? null}
                    defaultExpanded={false}
                    onChanged={onChanged}
                  />
                ))}
              </div>
            </details>
          ) : null}

          <div ref={payablesRef}>
            <AccountsPayableBlock student={student} />
          </div>

          <div ref={orphansRef}>
            <OrphanInvoicesBlock studentId={student.id} onChanged={onChanged} />
          </div>
        </div>
      );
    },
  ),
);

OverviewTab.displayName = 'OverviewTab';

const TerminatedSummary = styled.summary`
  cursor: pointer;
  font-size: 12px;
  color: ${StudentColors.textMuted};
  font-weight: 600;
  padding: 6px 0;
`;

const ExportRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 8px 0 4px;
`;

// keep the ref type available to the page shell
export type { IOverviewTabRefs };
